import { Injectable } from '@angular/core';
import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import { ReplaySubject, Subject, first, takeUntil } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { TrsFeatureFlags } from '../enums/trs-feature-flags.enum';
import { EnvironmentService } from './environment.service';

@Injectable()
export class FeatureFlagService {

  private ldClient: LDClient | null = null;
  public sdkReady$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private clientId: string;
  private environmentConfig: any = null;

  private isInit = false;
  disconnect$ = new Subject<boolean>();

  constructor(private environmentService: EnvironmentService, private authService: AuthService) {
    this.environmentService.environment$.pipe(takeUntil(this.disconnect$)).subscribe(env => {
      if (env) {
        this.environmentConfig = env;
        this.authService.refreshHeader$
          .pipe(first())
          .subscribe(() => {
            this.clientId = env.launchDarklyClientId;
            this.initLaunchDarkly();
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.disconnect$.next(true);
    this.disconnect$.unsubscribe();

    if (this.ldClient != null) {
      this.ldClient.flush();
    }
  }

  public initLaunchDarkly() {
    if (!this.isInit) {
      if (!!this.clientId) {
        console.log('using clientID: ' + this.clientId);
        this.isInit = true;

        const userContext = this.getUserContext();

        this.ldClient = initialize(
          this.clientId,
          userContext,
          {
            sendEventsOnlyForVariation: true,
            evaluationReasons: true,
            bootstrap: 'localStorage'
          }
        );

        this.ldClient.on('change', (flags) => {
          this.sdkReady$.next();
        });

        this.ldClient.on('ready', () => {
          this.sdkReady$.next();
          return this.ldClient;
        });
      } else {
        console.log('No clientId will not init');
      }
    } else {
      console.log('Already Init');
    }
  }

  checkFeatureFlagBoolean(flagKey: TrsFeatureFlags, fallback: boolean = false) {
    try {
      // Get environment variable fallback value if no fallback provided
      const envFallbackValue = fallback !== false ? fallback : this.getEnvironmentFallbackValue(flagKey, fallback);

      if (this.ldClient == null || this.ldClient === undefined) {
        return envFallbackValue;
      }

      const flagDetails = this.ldClient.variationDetail(flagKey, envFallbackValue);
      const flagValue = flagDetails.value;
      return flagValue;
    } catch (error) {
      console.warn(`FeatureFlagService.checkFeatureFlagBoolean - Error getting feature flag ${flagKey}, using fallback`, error);
      return this.getEnvironmentFallbackValue(flagKey, fallback);
    }
  }

  checkFeatureFlag(flagKey: TrsFeatureFlags, fallback = undefined) {
    try {
      // Get environment variable fallback value if no fallback provided
      const envFallbackValue = fallback !== undefined ? fallback : this.getEnvironmentFallbackValue(flagKey, fallback);

      if (this.ldClient == null || this.ldClient === undefined) {
        return envFallbackValue;
      }

      const flagDetails = this.ldClient.variationDetail(flagKey, envFallbackValue);
      const flagValue = flagDetails.value;
      return flagValue;
    } catch (error) {
      console.warn(`FeatureFlagService.checkFeatureFlag - Error getting feature flag ${flagKey}, using fallback`, error);
      return this.getEnvironmentFallbackValue(flagKey, fallback);
    }
  }

  getUserContext() {
    const userId = this.authService.getUniqueName();
    const fullName = this.authService.getFullName();
    const userNameSplit = fullName?.split(' ');
    const userFirstName = (!!userNameSplit && userNameSplit.length > 0) ? userNameSplit[0] : '';
    const userLastName = (!!userNameSplit && userNameSplit.length > 1) ? userNameSplit.slice(1).join(' ') : '';

    return {
      key: userId,
      anonymous: false,
      firstName: userFirstName,
      lastName: userLastName
    };
  }

  /**
   * Gets feature flag value from environment configuration (fallback mechanism)
   * @param flagKey The feature flag key from TrsFeatureFlags enum
   * @param defaultValue The default value if flag is not found in environment
   * @returns The feature flag value from environment or default
   */
  private getEnvironmentFallbackValue<T>(flagKey: TrsFeatureFlags, defaultValue: T): T {
    if (!this.environmentConfig) {
      return defaultValue;
    }

    // Map flag keys to environment config property names
    const envPropertyMap: Record<TrsFeatureFlags, string> = {
      [TrsFeatureFlags.NcmHeader]: 'trsNcmHeader'
    };

    const envProperty = envPropertyMap[flagKey];
    if (envProperty && this.environmentConfig.hasOwnProperty(envProperty)) {
      const value = this.environmentConfig[envProperty];
      return value !== undefined && value !== null ? value : defaultValue;
    }

    return defaultValue;
  }
}
